import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Brain, 
  Watch, 
  Compass, 
  Calendar, 
  BookOpen, 
  ArrowRight, 
  Sparkles,
  Users,
  Zap,
  Star,
  MessageCircle,
  Lightbulb,
  Heart,
  Target,
  Briefcase,
  UserCircle,
  Building,
  Gauge,
  Clock,
  Infinity
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import logoHED from '@/assets/logo-hed.png';

interface ToolVisibility {
  barometre: boolean;
  projectorWatch: boolean;
  align: boolean;
}

const DEFAULT_VISIBILITY: ToolVisibility = {
  barometre: true,
  projectorWatch: false,
  align: false
};

const testimonials = [
  {
    name: "Marion",
    role: "entrepreneure Projector",
    content: "Je ne comprenais pas pourquoi je m'épuisais. Le HED m'a offert une boussole, sans me forcer à changer.",
    avatar: "M"
  },
  {
    name: "Samir",
    role: "manager Generator",
    content: "Pour la première fois, j'ai eu une lecture de moi qui respectait ma nature. Et ça… ça change tout.",
    avatar: "S"
  },
  {
    name: "Julie",
    role: "thérapeute Manifesting Generator",
    content: "Grâce au HED, j'ai enfin compris mon rythme unique et comment l'honorer dans mon activité.",
    avatar: "J"
  }
];

const HomePage = () => {
  const { currentUser } = useAuth();
  const [toolVisibility, setToolVisibility] = useState<ToolVisibility>(DEFAULT_VISIBILITY);
  
  useEffect(() => {
    const loadToolVisibility = async () => {
      try {
        const { data: settings, error } = await supabase
          .from('admin_settings')
          .select('value')
          .eq('key', 'tool_visibility')
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading tool visibility:', error);
          return;
        }

        if (settings?.value) {
          setToolVisibility(settings.value as ToolVisibility);
        }
      } catch (error) {
        console.error('Error loading tool visibility:', error);
      }
    };

    loadToolVisibility();
  }, []);

  return (
    <div className="min-h-screen tool-background portal-background overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden px-4">
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?q=80&w=1999&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.3)'
          }}
        />

        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-1"
          style={{
            background: 'linear-gradient(to bottom, rgba(84, 7, 91, 0.9), rgba(147, 51, 234, 0.7))'
          }}
        />

        {/* Content */}
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 py-20 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            {/* Logo HED */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: [-5, 5],
                transition: {
                  y: {
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }
                }
              }}
              className="mb-12"
            >
              <motion.img 
                src={logoHED}
                alt="Logo HED"
                className="h-32 md:h-40 mx-auto filter brightness-0 invert"
              />
            </motion.div>

            <div className="space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Et si vous n'étiez pas ici par hasard ?
              </h1>
              
              <p className="text-lg sm:text-xl md:text-2xl font-light max-w-3xl mx-auto leading-relaxed">
                Une suite d'outils pour décoder votre énergie, retrouver votre rythme, et faire des choix alignés.
              </p>

              {/* CTA Button */}
              <div className="mt-12">
                <Button 
                  asChild
                  className="w-full sm:w-auto bg-white text-purple-900 hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Link to="/parcours">
                    Je commence le voyage
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Quick Access Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mt-20">
              <Link to="/barometre">
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 cursor-pointer hover:bg-white/20 transition-all duration-300 hover:shadow-lg"
                >
                  <Brain className="w-8 sm:w-10 h-8 sm:h-10 mb-4 mx-auto text-white" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Baromètre Énergétique</h3>
                  <p className="text-sm text-white/80">Faites le point sur votre état du jour</p>
                </motion.div>
              </Link>

              <Link to="/parcours">
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 cursor-pointer hover:bg-white/20 transition-all duration-300 hover:shadow-lg"
                >
                  <Compass className="w-8 sm:w-10 h-8 sm:h-10 mb-4 mx-auto text-white" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Je ne sais pas par où commencer</h3>
                  <p className="text-sm text-white/80">On vous guide pas à pas</p>
                </motion.div>
              </Link>

              <Link to="/lecture-design">
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 cursor-pointer hover:bg-white/20 transition-all duration-300 hover:shadow-lg"
                >
                  <Lightbulb className="w-8 sm:w-10 h-8 sm:h-10 mb-4 mx-auto text-white" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Lecture de design</h3>
                  <p className="text-sm text-white/80">Recevez votre profil énergétique</p>
                </motion.div>
              </Link>

              <Link to="/tirage-cartes">
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 cursor-pointer hover:bg-white/20 transition-all duration-300 hover:shadow-lg"
                >
                  <Star className="w-8 sm:w-10 h-8 sm:h-10 mb-4 mx-auto text-white" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Tirage de cartes</h3>
                  <p className="text-sm text-white/80">Recevez un message pour aujourd'hui</p>
                </motion.div>
              </Link>

              <Link to="/forum">
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 cursor-pointer hover:bg-white/20 transition-all duration-300 hover:shadow-lg"
                >
                  <MessageCircle className="w-8 sm:w-10 h-8 sm:h-10 mb-4 mx-auto text-white" />
                  <h3 className="text-base sm:text-lg font-semibold mb-2">Accès au forum</h3>
                  <p className="text-sm text-white/80">Un espace doux pour échanger</p>
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 2 - Pourquoi le HED */}
      <section className="py-20 px-4 bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Un outil de clarté, pas une recette miracle.</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Le Human & Entity Design (HED) est né d'un besoin profond :
              celui de créer un langage commun entre notre monde intérieur et nos réalités extérieures.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Ni dogmatique, ni théorique, il s'adapte à votre vécu et vous accompagne là où vous en êtes,
                avec douceur, précision… et sans jugement.
              </p>
              <div className="bg-purple-50 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-6 h-6 text-purple-600" />
                  <p className="text-purple-900 font-medium">
                    Que vous soyez en transition, en quête de sens ou à la croisée des chemins,
                    le HED éclaire vos choix et respecte votre rythme.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1470115636492-6d2b56f9146d?q=80&w=2070&auto=format&fit=crop"
                alt="Méditation et clarté"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - Je suis... */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Choisissez votre point de départ</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <UserCircle className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                <h3 className="text-xl font-bold mb-4">Je suis en quête de moi-même</h3>
                <p className="text-gray-600 mb-6">
                  Et j'ai besoin d'un cadre pour comprendre mon énergie, mes émotions, mes schémas.
                </p>
                <Button asChild className="w-full">
                  <Link to="/parcours">Explorer mon parcours</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <Briefcase className="w-12 h-12 mx-auto mb-4 text-blue-600" />
                <h3 className="text-xl font-bold mb-4">Je suis coach ou thérapeute</h3>
                <p className="text-gray-600 mb-6">
                  Et je veux intégrer une nouvelle grille de lecture puissante dans mes accompagnements.
                </p>
                <Button asChild className="w-full">
                  <Link to="/parcours">Explorer mon parcours</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <Building className="w-12 h-12 mx-auto mb-4 text-green-600" />
                <h3 className="text-xl font-bold mb-4">Je fais partie d'un collectif</h3>
                <p className="text-gray-600 mb-6">
                  Et je cherche à mieux comprendre les dynamiques humaines de mon équipe.
                </p>
                <Button asChild className="w-full">
                  <Link to="/parcours">Explorer mon parcours</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 5 - Témoignages */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">
            Des parcours uniques. Un point commun : l'alignement.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-xl font-bold text-purple-600">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h3 className="font-bold">{testimonial.name}</h3>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.content}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6 - Communauté */}
      <section className="py-20 px-4 bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12">Une communauté douce et puissante</h2>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Rejoignez un espace d'échange bienveillant, sans performance, où chacun avance à son rythme.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                <h3 className="text-xl font-bold mb-2">Forum privé</h3>
                <p className="text-gray-600">Échangez avec d'autres personnes sur leur parcours</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                <h3 className="text-xl font-bold mb-2">Ressources exclusives</h3>
                <p className="text-gray-600">Accédez à notre bibliothèque de contenus</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-purple-600" />
                <h3 className="text-xl font-bold mb-2">Webinaires & cercles</h3>
                <p className="text-gray-600">Participez à nos événements en direct</p>
              </CardContent>
            </Card>
          </div>

          <Button size="lg" className="bg-[#54075B] hover:bg-[#54075B]/90">
            Accéder à la communauté
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Section 7 - Call to Action */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white overflow-hidden">
            <CardContent className="p-8 sm:p-12">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">Laissez-vous guider, en conscience.</h2>
              <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto">
                Un accompagnement personnalisé est possible.
                Sous forme de lecture de design, de parcours immersif ou de diagnostic collectif.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  Réserver une séance
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button variant="outline" size="lg" className="w-full sm:w-auto text-white border-white hover:bg-white/10">
                  Explorer les consultations
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default HomePage;